import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { FORGE } from "@/constants/testIds";

/**
 * AgentCard — a collapsible card that shows a single agent's streaming output.
 * status: "idle" | "active" | "done"
 */
export default function AgentCard({
    agent,
    index,
    status,
    output,
    defaultOpen = false,
}) {
    const [open, setOpen] = useState(defaultOpen);
    const isActive = status === "active";
    const isDone = status === "done";
    const isIdle = status === "idle";

    return (
        <div
            data-testid={FORGE.agentCard(agent.id)}
            data-status={status}
            className={`bg-white rounded-lg border transition-colors duration-300 card-ambient ${
                isActive
                    ? "border-[color:var(--profecia-gold-subtle)]"
                    : "border-[color:var(--border-subtle)]"
            }`}
        >
            <button
                type="button"
                data-testid={FORGE.agentToggle(agent.id)}
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-[color:var(--surface-hover)] transition-colors duration-200 rounded-lg"
            >
                <span
                    className={`font-mono text-xs w-10 shrink-0 ${
                        isDone
                            ? "text-[color:var(--profecia-gold)]"
                            : "text-[color:var(--text-muted)]"
                    }`}
                >
                    /0{index + 1}
                </span>
                <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3 flex-wrap">
                        <h3 className="font-serif text-xl text-[color:var(--text-main)] leading-tight">
                            {agent.name}
                        </h3>
                        <span className="text-xs text-[color:var(--text-muted)] font-light">
                            {agent.desc}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <span
                        className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded ${
                            isActive
                                ? "bg-[color:var(--profecia-gold-light)] text-[color:var(--profecia-gold)]"
                                : isDone
                                  ? "bg-[color:var(--profecia-gold)] text-white"
                                  : "text-[color:var(--text-muted)]"
                        }`}
                    >
                        {isActive
                            ? "streaming"
                            : isDone
                              ? "done"
                              : "idle"}
                    </span>
                    <ChevronDown
                        size={16}
                        className={`text-[color:var(--text-muted)] transition-transform duration-200 ${
                            open ? "rotate-180" : ""
                        }`}
                    />
                </div>
            </button>
            {open && (
                <div className="px-5 pb-5 pt-1">
                    <div
                        data-testid={FORGE.agentOutput(agent.id)}
                        className="font-mono text-[13px] leading-relaxed text-[color:var(--text-main)] whitespace-pre-wrap bg-[color:var(--surface-hover)] rounded-md p-4 border border-[color:var(--border-subtle)] min-h-[80px]"
                    >
                        {output ? (
                            <>
                                {output}
                                {isActive && <span className="stream-cursor" />}
                            </>
                        ) : isIdle ? (
                            <span className="text-[color:var(--text-muted)] italic font-sans text-sm">
                                Waiting for upstream agents…
                            </span>
                        ) : (
                            <span className="stream-cursor" />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
